import { calcCardSelectableInfo, getCardPrice, WriteOffCardUserList } from '@/models/card';
import { DateTimeFormat } from '@/models/common';
import { Coupon, getCouponPrice } from '@/models/coupon';
import { hasDeliveryType, OrderDeliveryType, OrderType } from '@/models/order';
import { PaymentChannel, ReceivingMode } from '@/models/payment';
import { PointRules } from '@/models/point';
import { calcSpecificationsDeliveryType, calcSpecificationsTotalPrice } from '@/models/pos';
import { GOODS_TYPE } from '@/models/sku';
import BigNumber from 'bignumber.js';
import dayjs from 'dayjs';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { usePreOrderStore } from '../../store/usePreOrderStore';
import { calcPointConsume } from '../PointConsume';

/**
 * POS 结算模块状态管理 Store (Zustand)
 *
 * 替代原先以 antd Form 实例作为全局状态桶和大量 <Form.Item noStyle Input hidden /> 的低效实现。
 * 集中管理 POS 结算在不同模式（购物车/购卡/充值/直收）下的金额计算、优惠券抵扣、套餐卡核销、积分抵扣与提交 Payload 构建。
 */
export interface SettlementState {
  // === 结算上下文 ===
  /** 结算模式（直收、充值、购卡、购物车结算；INVALID 表示结算弹窗关闭，即弹窗开关） */
  mode: (typeof ReceivingMode)[keyof typeof ReceivingMode];
  /** 当前商户的积分规则（配置快照，来自 POSBaseInfoContext；购物车数据 preOrder 由 usePreOrderStore 提供，不在此镜像） */
  pointRules: PointRules | null;

  // === 用户交互与表单输入状态 ===
  /** 选中的支付渠道（在线扫码、现金、美团验券、抖音验券等） */
  paymentChannel: (typeof PaymentChannel)[keyof typeof PaymentChannel];
  /** 客户付款码 (扫码支付时输入) */
  authCode: string;
  /** 现金收银键盘输入的实收金额 */
  cashReceived: BigNumber | number | null;
  /** 特殊减免/抹零金额 */
  waiverAmount: string | number;
  /** 本次消费使用的积分数量 */
  pointNum: number;
  /** 使用客户账户余额支付的金额 */
  balancePrice: BigNumber | null;
  /** 客户账户6位支付密码 (充值/余额相关) */
  balanceCode: string;
  /** 选中的优惠券 */
  selectedCoupon: Coupon | null;
  /** 选中的套餐卡及抵扣数量映射关系 { [cardUserId]: count } */
  selectedCards: { [cardUserId: number]: number } | null;
  /** 充值赠送金额 */
  rechargePlusPrice: BigNumber | number | null;

  // === 自动计算/派生的结算金额字段（原 hidden Form.Item） ===
  /** 商品总价 */
  goodsPrice: BigNumber;
  /** 套餐卡抵扣金额 */
  cardPrice: BigNumber | undefined;
  /** 待核销套餐卡与商品映射列表 */
  writeOffCardUserList: WriteOffCardUserList | undefined;
  /** 优惠券抵扣金额 */
  couponPrice: BigNumber | undefined;
  /** 积分抵扣金额 */
  pointPrice: BigNumber | undefined;
  /** 扣除套餐卡与优惠券后的待扣积分前价格 */
  beforePointPrice: BigNumber;
  /** 抹零前的应付总额 */
  beforeTotalPrice: BigNumber;
  /** 客户视角结算总额（包含余额支付金额） */
  totalPrice: BigNumber;
  /** 商户视角实收金额（扣除余额支付后的现金/扫码应收额） */
  actualPrice: BigNumber | undefined;
  /** 订单交付类型（到店服务等） */
  deliveryType: (typeof OrderDeliveryType)[keyof typeof OrderDeliveryType];
  /** 关联预约 ID */
  appointmentId?: number;

  // === Actions 与状态变更方法 ===
  /** 打开结算弹窗（进入指定模式）；结算上下文由 Settlement 组件在 mode 变化后经 init 全量初始化 */
  open: (mode: (typeof ReceivingMode)[keyof typeof ReceivingMode]) => void;
  /** 关闭结算弹窗：购卡模式先清空待购套餐卡（取消购买），再退出结算模式 */
  close: () => void;
  /** 校验购物车非空并进入购物车结算；空车返回 false */
  checkout: () => boolean;
  /**
   * 初始化/重建结算状态：全量重置所填所选与金额，再按当前上下文重算。
   * 调用时机：弹窗挂载（打开即干净表单）、用户信息或购物车内容变化时
   * （业务规则：上下文变化 → 所填所选重置）。保留当前 mode 与 pointRules（未显式传入时）。
   * CART 模式下重建后会按当前购物车与用户持有套餐卡自动登记期望核销值（自动选择），
   * 由流程层确定性执行，不依赖组件挂载时序。
   */
  init: (pointRules?: PointRules) => void;
  /** 重置所有结算 Store 状态 */
  reset: () => void;
  /** 设置支付渠道 */
  setPaymentChannel: (channel: (typeof PaymentChannel)[keyof typeof PaymentChannel]) => void;
  /** 设置付款码 */
  setAuthCode: (code: string) => void;
  /** 设置现金实收金额 */
  setCashReceived: (cash: BigNumber | number | null) => void;
  /** 设置抹零金额 */
  setWaiverAmount: (amount: string | number) => void;
  /** 设置使用积分数量 */
  setPointNum: (num: number) => void;
  /** 设置余额支付金额 */
  setBalancePrice: (price: BigNumber | null) => void;
  /** 设置支付密码 */
  setBalanceCode: (code: string) => void;
  /** 设置选择的优惠券 */
  setSelectedCoupon: (coupon: Coupon | null) => void;
  /** 设置选择的套餐卡 */
  setSelectedCards: (cards: { [cardUserId: number]: number } | null) => void;
  /** 设置待收款金额（用于直收/充值模式） */
  setActualPrice: (price: BigNumber | number | string | null) => void;
  /** 设置充值赠送金额 */
  setRechargePlusPrice: (price: BigNumber | number | null) => void;

  /** 根据当前选中的商品、优惠券、套餐卡、积分与抹零规则重新计算全部结算金额 */
  recalculatePrices: (
    /**是否保留抹零金额，默认 false 不保留，抹零金额将重置为0 */
    keepWaiverAmount?: boolean
  ) => void;
  /** 导出发送给后端 `/trade/pay` API 的格式化请求 Payload */
  getSubmitPayload: () => Record<string, any>;
}

const createInitialState = () =>
  ({
    authCode: '',
    cashReceived: null,
    waiverAmount: null,
    pointNum: 0,
    balancePrice: null,
    balanceCode: '',
    selectedCoupon: null,
    selectedCards: null,
    rechargePlusPrice: null,

    goodsPrice: new BigNumber(0),
    cardPrice: undefined,
    writeOffCardUserList: undefined,
    couponPrice: undefined,
    pointPrice: undefined,
    beforePointPrice: new BigNumber(0),
    beforeTotalPrice: new BigNumber(0),
    totalPrice: new BigNumber(0),
    actualPrice: undefined,
    deliveryType: OrderDeliveryType.INVALID,

    mode: ReceivingMode.INVALID,
    pointRules: null,
    paymentChannel: PaymentChannel.Online,
  }) as SettlementState;

const actionName = (name: string) => `settlement/${name}`;

export const useSettlementStore = create<SettlementState>()(
  devtools(
    (set, get) => ({
      ...createInitialState(),

      init: (pointRules?: PointRules | null) => {
        const { mode, pointRules: currentPointRules } = get();
        // 自动选择套餐卡：重建后按当前购物车与用户持有卡登记期望核销值
        // （业务规则：上下文变化 → 所填所选重置；系统按新上下文自动匹配，UI 可手动调整）
        let selectedCards: { [cardUserId: number]: number } | null = null;
        if (mode === ReceivingMode.CART) {
          const { preOrder } = usePreOrderStore.getState();
          const cardList = preOrder.userFullInfo?.cardList;
          if (cardList && cardList.length) {
            selectedCards = {};
            cardList.forEach(card => {
              const { available, max } = calcCardSelectableInfo(card, preOrder.specifications);
              if (available && max > 0) selectedCards![card.id] = max;
            });
            if (!Object.keys(selectedCards).length) selectedCards = null;
          }
        }
        set(
          {
            ...createInitialState(),
            // 弹窗开关不因重建改变（从 get() 读当前值，而非闭包旧值）
            mode,
            // 未显式传入时保留现有积分规则（上下文变化触发重建的场景）
            pointRules: pointRules === undefined ? currentPointRules : pointRules,
            selectedCards,
          },
          false,
          actionName('init')
        );
        // 重建后按当前上下文重新计算价格
        get().recalculatePrices();
      },

      reset: () => {
        set(createInitialState(), false, actionName('reset'));
      },

      open: mode => {
        set({ mode }, false, actionName('open'));
      },

      close: () => {
        const { mode } = get();
        if (mode === ReceivingMode.CARD) {
          // 结算窗口关闭时，清空要购买的套餐卡 (取消购买)
          usePreOrderStore.getState().clearCards();
        }
        // 关闭即清理：重置全部表单状态与模式，保证每次打开是干净表单
        set(createInitialState(), false, actionName('close'));
      },

      checkout: () => {
        const { preOrder } = usePreOrderStore.getState();
        if (!preOrder.specifications || !preOrder.specifications.length) {
          return false;
        }
        get().open(ReceivingMode.CART);
        return true;
      },

      setPaymentChannel: paymentChannel => set({ paymentChannel }, false, actionName('paymentChannel')),
      setAuthCode: authCode => set({ authCode }, false, actionName('authCode')),
      setCashReceived: cashReceived => set({ cashReceived }, false, actionName('cashReceived')),
      setWaiverAmount: waiverAmount => {
        set({ waiverAmount }, false, actionName('waiverAmount'));
        get().recalculatePrices(true);
      },
      setPointNum: pointNum => {
        set({ pointNum }, false, actionName('pointNum'));
        get().recalculatePrices();
      },
      setBalancePrice: balancePrice => {
        set({ balancePrice }, false, actionName('balancePrice'));
        get().recalculatePrices(true);
      },
      setBalanceCode: balanceCode => set({ balanceCode }, false, actionName('balanceCode')),
      setSelectedCoupon: selectedCoupon => {
        set({ selectedCoupon }, false, actionName('selectedCoupon'));
        get().recalculatePrices();
      },
      setSelectedCards: selectedCards => {
        set({ selectedCards }, false, actionName('selectedCards'));
        get().recalculatePrices();
      },
      setActualPrice: price => {
        const actualPrice = price !== null && price !== undefined ? new BigNumber(price) : undefined;
        set({ actualPrice }, false, actionName('actualPrice'));
        get().recalculatePrices();
      },
      setRechargePlusPrice: price => {
        const rechargePlusPrice = price !== null && price !== undefined ? new BigNumber(price) : null;
        set({ rechargePlusPrice }, false, actionName('rechargePlusPrice'));
      },

      /**
       * 核心算法：计算多种结算模式下的动态金额分布
       * 顺序：商品总价 -> 套餐卡抵扣 -> 优惠券抵扣 -> 积分抵现 -> 特殊抹零减免 -> 余额支付 -> 最终实付款
       */
      recalculatePrices: (keepWaiverAmount = false) => {
        // 购物车数据从数据域读取（不持有镜像）
        const { preOrder } = usePreOrderStore.getState();
        const { mode, pointRules, selectedCoupon, selectedCards, pointNum, balancePrice, waiverAmount } = get();

        let revisedWaiverAmount = waiverAmount;
        if (!keepWaiverAmount) {
          // waiverAmount 重置为 0
          revisedWaiverAmount = null;
        }

        if (mode === ReceivingMode.DIRECT || mode === ReceivingMode.RECHARGE) {
          // 直收与充值模式无交付类型
          set(
            {
              deliveryType: OrderDeliveryType.INVALID,
            },
            false,
            actionName('deliveryType')
          );
        } else if (mode === ReceivingMode.CART && preOrder.specifications) {
          // 1. 计算交付类型（如包含到店服务）
          const deliveryType = calcSpecificationsDeliveryType(preOrder.specifications);

          // 2. 计算购物车商品原价总额
          const goodsPrice = calcSpecificationsTotalPrice(preOrder.specifications);

          // 3. 计算套餐卡抵扣金额与待核销列表
          let { cardPrice, writeOffCardUserList } = getCardPrice({
            specifications: preOrder.specifications,
            userFullInfo: preOrder.userFullInfo,
            selectedCards: selectedCards,
          });

          // 4. 抵扣套餐卡金额后，再计算优惠券抵扣价格
          const couponPrice = selectedCoupon
            ? getCouponPrice(selectedCoupon, goodsPrice.minus(cardPrice))
            : new BigNumber(0);

          // 5. 卡券抵扣后的基准价（下限 0：卡券抵扣不超商品价，减免券溢出由流程层兜底）
          let beforePointPrice = goodsPrice.minus(couponPrice).minus(cardPrice);
          if (beforePointPrice.isLessThan(0)) {
            beforePointPrice = new BigNumber(0);
          }

          // 6. 积分抵扣（钳制逻辑见 calcPointConsume：不超基准价、应付不为负，
          //    超限时反向折算并同步修正 pointNum，保持 pointNum ↔ pointPrice 一致）
          const { pointPrice, revisedPointNum } = calcPointConsume(pointNum, pointRules, beforePointPrice);
          const curBalance = balancePrice ? new BigNumber(balancePrice) : new BigNumber(0);
          const beforeTotalPrice = beforePointPrice.minus(pointPrice);

          // 7. 抹零钳制（不超过积分抵扣后的应付）
          let revisedWaiver =
            revisedWaiverAmount === null || revisedWaiverAmount === undefined
              ? null
              : new BigNumber(revisedWaiverAmount);
          if (revisedWaiver && revisedWaiver.gt(beforeTotalPrice)) {
            revisedWaiver = beforeTotalPrice;
          }

          // 付款总额 (客户视角)：扣除抹零后的金额
          const totalPrice = beforeTotalPrice.minus(revisedWaiver || 0);
          // 8. 实付款 (商户视角)：扣除余额支付后，实际需要通过现金/扫码录入的金额
          const actualPrice = totalPrice.minus(curBalance);

          set(
            {
              goodsPrice,
              cardPrice: cardPrice.comparedTo(0) > 0 ? cardPrice : undefined,
              writeOffCardUserList: cardPrice.comparedTo(0) > 0 ? writeOffCardUserList : undefined,
              couponPrice: couponPrice.comparedTo(0) > 0 ? couponPrice : undefined,
              pointPrice: pointPrice.comparedTo(0) > 0 ? pointPrice : undefined,
              balancePrice: curBalance.comparedTo(0) > 0 ? curBalance : undefined,
              beforePointPrice,
              beforeTotalPrice,
              totalPrice,
              actualPrice,
              deliveryType,
              appointmentId: preOrder.appointment?.id,
              // 钳制后的用户输入写回（pointNum 与 pointPrice 保持一致；waiverAmount 不超应付）
              pointNum: revisedPointNum,
              waiverAmount: revisedWaiver === null ? null : revisedWaiver.toNumber(),
            },
            false,
            actionName('calcPrice')
          );
        } else if (mode === ReceivingMode.CARD && preOrder.cards) {
          // 购买套餐卡模式结算
          const goodsPrice = preOrder.cards.reduce((acc, cur) => {
            return acc.plus(new BigNumber(cur.price).multipliedBy(1));
          }, new BigNumber(0));

          const actualPrice = goodsPrice.minus(new BigNumber(revisedWaiverAmount || 0));

          set(
            {
              beforeTotalPrice: goodsPrice,
              goodsPrice: new BigNumber(goodsPrice),
              actualPrice,
              totalPrice: new BigNumber(actualPrice),
              deliveryType: OrderDeliveryType.INVALID,
              waiverAmount: revisedWaiverAmount,
            },
            false,
            actionName('calcPrice')
          );
        }
      },

      getSubmitPayload: () => {
        // 购物车数据从数据域读取（不持有镜像）
        const { preOrder } = usePreOrderStore.getState();
        const userFullInfo = preOrder.userFullInfo;
        const {
          mode,
          paymentChannel,
          authCode,
          actualPrice,
          goodsPrice,
          cardPrice,
          writeOffCardUserList,
          couponPrice,
          pointPrice,
          pointNum,
          balancePrice,
          waiverAmount,
          deliveryType,
          appointmentId,
          selectedCoupon,
          balanceCode,
          rechargePlusPrice,
        } = get();

        const values: Record<string, any> = {
          paymentChannel,
          authCode,
          actualPrice,
          goodsPrice,
          cardPrice,
          writeOffCardUserList,
          couponPrice,
          pointPrice,
          pointNum,
          balancePrice,
          waiverAmount,
          deliveryType,
          appointmentId,
          balanceCode,
          rechargePlusPrice,
        };

        // 交付类型中是否包含【到店服务】，若包含，则需要提交 服务开始、结束时间
        const hasServiceDeliveryType = hasDeliveryType(deliveryType, OrderDeliveryType.IN_STORE_SERVICE);
        values.serviceStartTime = hasServiceDeliveryType ? preOrder?.startTime : undefined;
        values.serviceEndTime = values.serviceStartTime ? dayjs().format(DateTimeFormat) : undefined;

        if (mode === ReceivingMode.CART && preOrder) {
          values.orderType = OrderType.Normal;
          const specifications = preOrder.specifications || [];
          values.cartList = specifications.map(v => ({
            id: v.goodsId,
            name: v.goodsName,
            type: v.goodsType,
            specificationId: v.id,
            specificationName: v.name,
            specificationCost: v.cost,
            number: v.number,
            price: v.price,
            picUrl: v.picUrl || v.goodsPicUrl,
          }));

          if (writeOffCardUserList) {
            values.writeOffCardUserList = (writeOffCardUserList as WriteOffCardUserList).map(card => ({
              cardUserId: card.cardUserId,
              number: card.number,
              specificationId: card.specificationId,
            }));
          }

          if (preOrder.operator) {
            values.operatorId = preOrder.operator.id;
          }
        } else if (mode === ReceivingMode.CARD && preOrder) {
          values.orderType = OrderType.Card;
          values.cartList = (preOrder.cards || []).map(card => ({
            id: card.id,
            name: card.name,
            type: GOODS_TYPE.CARD,
            specificationId: 0,
            number: 1,
            price: card.price,
          }));
        } else if (mode === ReceivingMode.RECHARGE) {
          values.orderType = OrderType.Recharge;
          values.rechargePrice = values.actualPrice;
        } else if (mode === ReceivingMode.DIRECT) {
          values.orderType = OrderType.Pay;
        }

        if (selectedCoupon) {
          values.couponUserId = selectedCoupon.id;
          values.couponId = selectedCoupon.cid;
        }

        if (userFullInfo && userFullInfo.user) {
          values.userId = userFullInfo.user.id;
        }

        return values;
      },
    }),
    {
      name: 'SettlementStore',
    }
  )
);
